import { newCreateWavArrayBuffer, newMergeFloat32ArrayToInt16 } from '../merge'

interface RecorderOptions {
  sampleRate?: number // 기본값: 44100
  maxRecordTimeMs?: number // 기본값: 5분(300,000 ms)
  waveformUpdateInterval?: number // ms 단위 (기본값 50ms)
  onWaveFormData?: (data: number[]) => void // Waveform UI 표시
  isSpeaking?: (isSpeaking: boolean) => void // Waveform 인식
  onStart?: () => void // 시작 시점 콜백
  onStop?: () => void // 정상 종료 시점 콜백
  onCancel?: () => void // 사용자가 취소했을 때 콜백
  onError?: (err: any) => void // 에러 발생 시 콜백
}

export class WebAudioRecorder {
  private audioCtx: AudioContext | null = null
  private mediaStream: MediaStream | null = null
  private analyser: AnalyserNode | null = null
  private audioWorkletNode: AudioWorkletNode | null = null
  private buffers: Float32Array[] = []
  private isRecording = false
  private cleanupTimer: any = null
  private waveformIntervalId: number | null = null
  private timeDomainData: Uint8Array | null = null

  private sampleRate: number
  private maxRecordTimeMs: number
  private waveformUpdateInterval: number

  // 콜백 함수들
  private onWaveFormData?: (data: number[]) => void
  private isSpeaking?: (isSpeaking: boolean) => void
  private onStart?: () => void
  private onStop?: () => void
  private onCancel?: () => void
  private onError?: (err: any) => void

  constructor(options: RecorderOptions = {}) {
    this.sampleRate = options.sampleRate ?? 44100
    this.maxRecordTimeMs = options.maxRecordTimeMs ?? 5 * 60 * 1000 // 기본 5분
    this.waveformUpdateInterval = options.waveformUpdateInterval ?? 100
    this.onWaveFormData = options.onWaveFormData
    this.isSpeaking = options.isSpeaking
    this.onStart = options.onStart
    this.onStop = options.onStop
    this.onCancel = options.onCancel
    this.onError = options.onError
  }

  /**
   * 내부적으로 analyser에서 파형 데이터를 가져와서 UI에 반영할 형태로 가공
   */
  private processWaveform(): number[] {
    if (!this.analyser || !this.timeDomainData) return []

    // const bufferLength = this.analyser.frequencyBinCount
    this.analyser.getByteTimeDomainData(this.timeDomainData) // [0..255] 값을 리턴

    const bufferLength = this.timeDomainData.length
    const targetLength = 10
    const chunkSize = Math.floor(bufferLength / targetLength)

    const downsampled: number[] = []

    for (let i = 0; i < targetLength; i++) {
      let sum = 0
      let count = 0
      // Accumulate all samples in the chunk
      for (let j = 0; j < chunkSize; j++) {
        const sampleIndex = i * chunkSize + j
        if (sampleIndex >= bufferLength) break
        // Center around 128 => amplitude around 0
        const centered = this.timeDomainData[sampleIndex] - 128
        // Convert from [-128..127] -> [-1..1]
        const normalized = centered / 128
        sum += Math.abs(normalized)
        count++
      }

      const avg = count > 0 ? sum / count : 0

      downsampled.push(Math.min(avg * 20, 1))
    }

    return downsampled
  }

  private detectSpeech(threshold = 0.01): boolean {
    if (!this.analyser) return false

    // AnalyserNode에서 time-domain 데이터 가져오기
    const bufferLength = this.analyser.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)
    this.analyser.getByteTimeDomainData(dataArray)

    // 0 ~ 255 -> -1 ~ +1 변환
    const normalizedData = Array.from(dataArray, (v) => (v - 128) / 128)

    // 간단히 peak(최대 진폭) 계산
    const peak = Math.max(...normalizedData.map(Math.abs))

    // peak이 임계치보다 크면 "말하고 있다"로 판별
    return peak >= threshold
  }

  /**
   * 녹음 시작
   */
  public async startRecording(): Promise<void> {
    if (this.isRecording) {
      console.warn('이미 녹음 중입니다.')
      return
    }
    this.isRecording = true
    this.buffers = []

    try {
      if ((navigator as any).audioSession) (navigator as any).audioSession.type = 'play-and-record'
      // 마이크 스트림 요청
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleSize: 16,
          channelCount: 1,
          echoCancellation: false,
          noiseSuppression: true,
        },
        video: false,
      })

      // AudioContext 생성
      this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: this.sampleRate,
      })

      // MediaStreamSource 생성
      const source = this.audioCtx.createMediaStreamSource(this.mediaStream)

      // AnalyserNode (파형 시각화용)
      this.analyser = this.audioCtx.createAnalyser()
      this.analyser.fftSize = 64
      this.analyser.smoothingTimeConstant = 0.8
      source.connect(this.analyser)

      const bufferLength = this.analyser.frequencyBinCount
      this.timeDomainData = new Uint8Array(bufferLength)

      // AudioWorklet 추가
      // *주의*: 아래 경로를 실제 프로젝트 구조에 맞게 수정하세요.
      await this.audioCtx.audioWorklet.addModule('/audio-processor-4.js')
      this.audioWorkletNode = new AudioWorkletNode(this.audioCtx, 'recorder-processor')
      source.connect(this.audioWorkletNode)

      // PCM 데이터 수신
      this.audioWorkletNode.port.onmessage = (event) => {
        if (!this.isRecording) return // 이미 취소/정지된 상태라면 무시
        if (event.data && event.data.type === 'data') {
          const floatBuffer: Float32Array = event.data.buffer
          this.buffers.push(floatBuffer)
        }
      }

      // 주기적으로 Waveform 데이터 갱신
      this.waveformIntervalId = window.setInterval(() => {
        if (!this.isRecording) return
        const wf = this.processWaveform()
        // const isSpeaking = this.detectSpeech()
        // this.isSpeaking?.(isSpeaking)
        this.onWaveFormData?.(wf)
      }, this.waveformUpdateInterval)

      // 자동 종료 타이머 (예: 5분)
      this.cleanupTimer = setTimeout(() => {
        this.stopRecording().catch((err) => {
          console.error('자동 종료 중 에러:', err)
        })
      }, this.maxRecordTimeMs)

      // 콜백
      this.onStart?.()
    } catch (err) {
      this.onError?.(err)
      this.isRecording = false
      this.cleanupResources()
    }
  }

  /**
   * 정상적으로 녹음을 종료하고, WAV ArrayBuffer를 반환
   */
  public async stopRecording(): Promise<ArrayBuffer | null> {
    if (!this.isRecording) {
      console.warn('녹음 중이 아닙니다.')
      return null
    }

    // 녹음 중지 상태
    this.isRecording = false

    // 자동 종료 타이머 해제
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer)
      this.cleanupTimer = null
    }

    // WAV 변환
    const pcmInt16 = newMergeFloat32ArrayToInt16(this.buffers)
    const wavData = newCreateWavArrayBuffer(pcmInt16, this.sampleRate)

    // 자원 정리
    this.cleanupResources()

    // onStop 콜백
    this.onStop?.()

    return wavData
  }

  /**
   * 사용자가 취소를 누른 경우
   * -> WAV 파일 생성/반환 없이 녹음 중단
   * -> onCancel 콜백 호출
   */
  public async cancelRecording(): Promise<void> {
    if (!this.isRecording) {
      console.warn('취소하려 했으나 녹음 중이 아닙니다.')
      return
    }

    this.isRecording = false

    // 자동 종료 타이머 해제
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer)
      this.cleanupTimer = null
    }

    // 파일 생성 없이 바로 정리
    this.cleanupResources()

    // 취소 콜백
    this.onCancel?.()
  }

  /**
   * 미디어 스트림, 오디오 노드 등 자원 정리
   */
  private cleanupResources() {
    if ((navigator as any).audioSession) (navigator as any).audioSession.type = 'playback'
    // Waveform Interval 정리
    if (this.waveformIntervalId) {
      clearInterval(this.waveformIntervalId)
      this.waveformIntervalId = null
    }

    // 2) 자동 종료 타이머 해제
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer)
      this.cleanupTimer = null
    }

    // stop tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop())
      this.mediaStream = null
    }

    // audio context
    if (this.audioCtx) {
      try {
        this.audioCtx.close()
      } catch (err) {
        console.error(err)
      }
      this.audioCtx = null
    }

    this.analyser = null
    if (this.audioWorkletNode) {
      this.audioWorkletNode.port.onmessage = null
      this.audioWorkletNode = null
    }

    // buffer 초기화
    this.buffers = []
  }
}
